import axios from 'axios';
import {
  clearCurrentSitePublicId,
  getCurrentSitePublicId,
} from './siteSession';
import {
  Employee,
  Qualification,
  EmployeeQualification,
  Operation,
  OperationQualificationRequirement,
  ProcessTemplate,
  ProcessStage,
  StageOperationSchedule,
  OperationConstraint,
  ConstraintValidationResult,
  Department,
  Team,
  EmployeeRole,
  EmployeeTeamRole,
  EmployeeUnavailability,
  ShiftType,
  OrgHierarchyResponse,
  EmployeeOrgContext,
  ShiftDefinition,
  BatchPlan,
  BatchTemplateSummary,
  BatchStatistics,
  MfgTemplatePackageDetail,
  MfgTemplatePackagePreview,
  MfgTemplatePackageSummary,
  HolidayServiceStatus,
  SchedulingSettings,
  SpecialShiftOccurrence,
  SpecialShiftWindow,
  SpecialShiftWindowDetail,
  SpecialShiftWindowPreview,
  SpecialShiftWindowRule,
} from '../types';

import { wxbToast } from '../components/wxb-ui/Toast/Toast';

const LEGACY_AUTH_TOKEN_STORAGE_KEY = 'auth_token';

/** Read the previous release's bearer once so it can be promoted to an HttpOnly cookie. */
export const readLegacyAuthTokenForMigration = (): string | null => {
  try {
    return localStorage.getItem(LEGACY_AUTH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

/** Remove tokens left by releases that persisted bearer credentials in Web Storage. */
export const clearLegacyAuthToken = (): void => {
  try {
    localStorage.removeItem(LEGACY_AUTH_TOKEN_STORAGE_KEY);
  } catch {
    /* noop */
  }
};

export {
  clearCurrentSitePublicId,
  getCurrentSitePublicId,
  setCurrentSitePublicId,
  subscribeToCurrentSiteChange,
} from './siteSession';

type SiteBoundRequestConfig = {
  __siteContextPublicId?: string;
};

const createSiteContextChangedError = () => {
  const error = new Error('Site context changed while the request was in flight');
  error.name = 'CanceledError';
  Object.assign(error, { code: 'ERR_CANCELED' });
  return error;
};

const getRequestSitePublicId = (config: unknown): string | null => {
  const value = (config as SiteBoundRequestConfig | undefined)?.__siteContextPublicId;
  return typeof value === 'string' && value ? value : null;
};

const rejectIfSiteContextChanged = (config: unknown): void => {
  const requestSitePublicId = getRequestSitePublicId(config);
  if (requestSitePublicId && requestSitePublicId !== getCurrentSitePublicId()) {
    throw createSiteContextChangedError();
  }
};

export const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  }
});

// 身份由同源 HttpOnly Cookie 携带；拦截器只负责 canonical Site path。
api.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  const headers = config.headers as Record<string, string>;
  headers['X-Requested-With'] = 'XMLHttpRequest';
  const currentSitePublicId = getCurrentSitePublicId();
  if (currentSitePublicId) {
    const requestUrl = config.url ?? '';
    const isSiteIndependent =
      requestUrl === '/auth'
      || requestUrl.startsWith('/auth/')
      || requestUrl === '/platform'
      || requestUrl.startsWith('/platform/')
      || requestUrl === '/health'
      || requestUrl.startsWith('/sites/');
    const isSiteBusinessRequest = !isSiteIndependent && requestUrl.startsWith('/');
    const isCanonicalSiteRequest = requestUrl.startsWith('/sites/');
    if (isSiteBusinessRequest || isCanonicalSiteRequest) {
      (config as typeof config & SiteBoundRequestConfig).__siteContextPublicId =
        currentSitePublicId;
    }
    if (isSiteBusinessRequest) {
      config.url = `/sites/${encodeURIComponent(currentSitePublicId)}${requestUrl}`;
    }
  }
  return config;
});

// 响应拦截器：
//   401 → 清 token 并跳 /login（登录接口本身的 401 不跳转，交由调用方处理报错文案）。
//   403 → Toast 提示无权限（错误信封可能带 required 权限码）。
api.interceptors.response.use(
  (response) => {
    rejectIfSiteContextChanged(response?.config);
    return response;
  },
  (error) => {
    try {
      rejectIfSiteContextChanged(error?.config);
    } catch (siteContextError) {
      return Promise.reject(siteContextError);
    }

    const status = error?.response?.status;
    const requestUrl: string = error?.config?.url ?? '';
    const isAuthEndpoint =
      requestUrl.includes('/auth/login')
      || requestUrl.includes('/auth/saml/')
      || requestUrl.includes('/auth/change-password')
      || requestUrl.includes('/auth/session-upgrade');

    if (status === 401 && !isAuthEndpoint) {
      clearLegacyAuthToken();
      clearCurrentSitePublicId();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    } else if (status === 403) {
      const data = error?.response?.data ?? {};
      const message = typeof data.error === 'string' && data.error ? data.error : '无权限执行该操作';
      wxbToast.error(message);
    }

    return Promise.reject(error);
  }
);

const mapEmployeePayload = (data: any) => ({
  employeeCode: data.employeeCode ?? data.employee_code,
  employeeName: data.employeeName ?? data.employee_name,
  departmentId: data.departmentId ?? data.department_id ?? null,
  primaryTeamId: data.primaryTeamId ?? data.primary_team_id ?? null,
  primaryRoleId: data.primaryRoleId ?? data.primary_role_id ?? null,
  employmentStatus: data.employmentStatus ?? data.employment_status ?? 'ACTIVE',
  hireDate: data.hireDate ?? data.hire_date ?? null,
  shopfloorBaselinePct: data.shopfloorBaselinePct ?? data.shopfloor_baseline_pct ?? null,
  shopfloorUpperPct: data.shopfloorUpperPct ?? data.shopfloor_upper_pct ?? null,
  orgRole: data.orgRole ?? data.org_role ?? 'FRONTLINE',
  employee_code: data.employeeCode ?? data.employee_code,
  employee_name: data.employeeName ?? data.employee_name,
  department_id: data.departmentId ?? data.department_id ?? null,
  primary_team_id: data.primaryTeamId ?? data.primary_team_id ?? null,
  primary_role_id: data.primaryRoleId ?? data.primary_role_id ?? null,
  employment_status: data.employmentStatus ?? data.employment_status ?? 'ACTIVE',
  hire_date: data.hireDate ?? data.hire_date ?? null,
  shopfloor_baseline_pct: data.shopfloorBaselinePct ?? data.shopfloor_baseline_pct ?? null,
  shopfloor_upper_pct: data.shopfloorUpperPct ?? data.shopfloor_upper_pct ?? null,
  org_role: data.orgRole ?? data.org_role ?? 'FRONTLINE',
});

export const employeeApi = {
  getAll: () => api.get<Employee[]>('/employees'),
  create: (data: Partial<Employee> & { employee_code?: string; employee_name?: string }) =>
    api.post('/employees', mapEmployeePayload(data)).then((res) => res.data),
  update: (id: number, data: Partial<Employee>) =>
    api.put(`/employees/${id}`, mapEmployeePayload(data)).then((res) => res.data),
  delete: (id: number) => api.delete(`/employees/${id}`),
  getAssignments: (employeeId: number) =>
    api.get<EmployeeTeamRole[]>(`/employees/${employeeId}/assignments`).then((res) => res.data),
  getReporting: (employeeId: number) =>
    api.get<{ leaderIds: number[]; directReportIds: number[] }>(`/employees/${employeeId}/reporting`).then((res) => res.data),
  updateReporting: (employeeId: number, data: { directReportIds: number[] }) =>
    api.put(`/employees/${employeeId}/reporting`, data).then((res) => res.data),
};

export const qualificationApi = {
  getAll: () => api.get<Qualification[]>('/qualifications'),
  create: (data: Qualification) => api.post<Qualification>('/qualifications', data),
  update: (id: number, data: Qualification) => api.put<Qualification>(`/qualifications/${id}`, data),
  delete: (id: number) => api.delete(`/qualifications/${id}`)
};

export interface EmployeeQualificationBatchChange {
  employee_id: number;
  qualification_id: number;
  qualification_level: number | null;
}

export interface EmployeeQualificationBatchResult {
  created: number;
  updated: number;
  deleted: number;
  unchanged: number;
}

export const employeeQualificationApi = {
  getAll: () => api.get<EmployeeQualification[]>('/employee-qualifications'),
  getByEmployeeId: (employeeId: number) => api.get<EmployeeQualification[]>(`/employee-qualifications/employee/${employeeId}`),
  create: (data: EmployeeQualification) => api.post<EmployeeQualification>('/employee-qualifications', data),
  update: (id: number, data: EmployeeQualification) => api.put<EmployeeQualification>(`/employee-qualifications/${id}`, data),
  delete: (id: number) => api.delete(`/employee-qualifications/${id}`),
  batchUpdate: (changes: EmployeeQualificationBatchChange[]) =>
    api.post<EmployeeQualificationBatchResult>('/employee-qualifications/batch', { changes }),
};

export const operationApi = {
  getAll: () => api.get<Operation[]>('/operations'),
  create: (data: Operation) => api.post<Operation>('/operations', data),
  update: (id: number, data: Operation) => api.put<Operation>(`/operations/${id}`, data),
  delete: (id: number) => api.delete(`/operations/${id}`)
};

export const operationQualificationRequirementApi = {
  getAll: () => api.get<OperationQualificationRequirement[]>('/operation-qualification-requirements'),
  create: (data: OperationQualificationRequirement) => api.post<OperationQualificationRequirement>('/operation-qualification-requirements', data),
  update: (id: number, data: OperationQualificationRequirement) => api.put<OperationQualificationRequirement>(`/operation-qualification-requirements/${id}`, data),
  delete: (id: number) => api.delete(`/operation-qualification-requirements/${id}`)
};

export const processTemplateApi = {
  getAll: () => api.get<ProcessTemplate[]>('/process-templates'),
  create: (data: ProcessTemplate) => api.post<ProcessTemplate>('/process-templates', data),
  update: (id: number, data: ProcessTemplate) => api.put<ProcessTemplate>(`/process-templates/${id}`, data),
  delete: (id: number) => api.delete(`/process-templates/${id}`)
};

export const processStageApi = {
  getAll: () => api.get<ProcessStage[]>('/process-stages'),
  create: (data: ProcessStage) => api.post<ProcessStage>('/process-stages', data),
  update: (id: number, data: ProcessStage) => api.put<ProcessStage>(`/process-stages/${id}`, data),
  delete: (id: number) => api.delete(`/process-stages/${id}`)
};

export const stageOperationScheduleApi = {
  getAll: () => api.get<StageOperationSchedule[]>('/stage-operation-schedules'),
  create: (data: StageOperationSchedule) => api.post<StageOperationSchedule>('/stage-operation-schedules', data),
  update: (id: number, data: StageOperationSchedule) => api.put<StageOperationSchedule>(`/stage-operation-schedules/${id}`, data),
  delete: (id: number) => api.delete(`/stage-operation-schedules/${id}`)
};

export const operationConstraintApi = {
  getAll: () => api.get<OperationConstraint[]>('/operation-constraints'),
  create: (data: OperationConstraint) => api.post<OperationConstraint>('/operation-constraints', data),
  update: (id: number, data: OperationConstraint) => api.put<OperationConstraint>(`/operation-constraints/${id}`, data),
  delete: (id: number) => api.delete(`/operation-constraints/${id}`),
  validateTemplate: (templateId: number) => api.get<ConstraintValidationResult>(`/constraints/template/${templateId}/validate`)
};

export type BatchPlanPayload = {
  batch_code: string;
  batch_name: string;
  template_id: number;
  project_code?: string | null;
  // 基准日期 Day0。后端按模板 min_day 自行换算最早工序日入库，前端不做偏移数学
  day0_date: string;
  plan_status?: BatchPlan['plan_status'];
  description?: string | null;
  notes?: string | null;
  // 二次确认后回传：清空下游排班/加班/冲突数据并按新模板强制重建（仅 update 使用）
  force_rebuild?: boolean;
};

export type MfgTemplatePackagePayload = {
  package_code?: string | null;
  package_name: string;
  description?: string | null;
  package_status?: 'DRAFT' | 'ACTIVE' | 'RETIRED';
  modules: Array<{
    role_code: string;
    role_name?: string | null;
    template_id: number;
    start_offset_days?: number | null;
    is_anchor?: boolean;
    sort_order?: number;
  }>;
  day_links?: Array<{
    source_role_code: string;
    target_role_code: string;
    source_anchor_day: number;
    target_anchor_day: number;
    lag_days?: number;
    description?: string | null;
    is_active?: boolean;
  }>;
};

export const mfgTemplatePackageApi = {
  list: () => api.get<MfgTemplatePackageSummary[]>('/mfg-template-packages').then((res) => res.data),
  get: (id: number) => api.get<MfgTemplatePackageDetail>(`/mfg-template-packages/${id}`).then((res) => res.data),
  preview: (id: number) => api.get<MfgTemplatePackagePreview>(`/mfg-template-packages/${id}/preview`).then((res) => res.data),
  create: (payload: MfgTemplatePackagePayload) =>
    api.post<MfgTemplatePackageDetail>('/mfg-template-packages', payload).then((res) => res.data),
  update: (id: number, payload: MfgTemplatePackagePayload) =>
    api.put<MfgTemplatePackageDetail>(`/mfg-template-packages/${id}`, payload).then((res) => res.data),
  remove: (id: number) => api.delete(`/mfg-template-packages/${id}`).then((res) => res.data),
};

// 「从模版刷新」预览/应用契约
export interface BatchRefreshOp {
  template_schedule_id: number;
  operation_id: number;
  operation_code: string | null;
  operation_name: string | null;
  stage_name: string | null;
  planned_start_datetime: string | null;
  planned_end_datetime: string | null;
  planned_duration: number | null;
  window_start_datetime: string | null;
  window_end_datetime: string | null;
  required_people: number | null;
}

export interface BatchRefreshChangedField {
  field: string;
  label: string;
  from: string | number | null;
  to: string | number | null;
}

export interface BatchRefreshRemovedOp extends BatchRefreshOp {
  is_locked: boolean;
}

export interface BatchRefreshChangedOp {
  template_schedule_id: number;
  operation_id: number;
  operation_code: string | null;
  operation_name: string | null;
  stage_name: string | null;
  is_locked: boolean;
  fields: BatchRefreshChangedField[];
}

export interface BatchRefreshPreview {
  batch: {
    id: number;
    batch_code: string;
    batch_name: string;
    template_id: number;
    template_name: string | null;
    plan_status: string;
    planned_start_date: string;
    day0_date: string | null;
  };
  canRefresh: boolean;
  revision: string;
  blockReason: string | null;
  summary: { added: number; removed: number; changed: number; unchanged: number; locked: number };
  relatedChanges: { constraints: boolean; shareGroups: boolean; resourceRules: boolean };
  added: BatchRefreshOp[];
  removed: BatchRefreshRemovedOp[];
  changed: BatchRefreshChangedOp[];
}

export interface BatchRefreshResult {
  success: boolean;
  added: number;
  removed: number;
  changed: number;
  replaced: number;
  overridesCleared: number;
  manualShareMembershipsCleared: number;
  manualShareGroupsCleared: number;
  manualShareGroupsRehomed: number;
  skippedLocked: number;
  constraintsRefreshed: boolean;
  shareGroupsRefreshed: boolean;
  resourceRulesRefreshed: boolean;
}

export const batchPlanApi = {
  list: () => api.get<any>('/batch-plans').then((res) => (res.data.data ? res.data.data : res.data)),
  getTemplates: () => api.get<BatchTemplateSummary[]>('/batch-plans/templates').then((res) => res.data),
  getStatistics: () => api.get<BatchStatistics>('/batch-plans/statistics').then((res) => res.data),
  create: (payload: BatchPlanPayload) => api.post<BatchPlan>('/batch-plans', payload).then((res) => res.data),
  createFromPackage: (payload: {
    mfg_package_id: number;
    batch_code: string;
    batch_name: string;
    planned_start_date: string;
    project_code?: string | null;
    description?: string | null;
    notes?: string | null;
  }) => api.post<{ message: string; batches: BatchPlan[] }>('/batch-plans/from-package', payload).then((res) => res.data),
  update: (id: number, payload: BatchPlanPayload) =>
    api.put<BatchPlan>(`/batch-plans/${id}`, payload).then((res) => res.data),
  remove: (id: number, options?: { force?: boolean }) =>
    api
      .delete(`/batch-plans/${id}`, {
        params: options?.force ? { force: true } : undefined,
      })
      .then((res) => res.data),
  activate: (id: number, options?: { color?: string }) =>
    api.post(`/batch-plans/${id}/activate`, options).then((res) => res.data),
  deactivate: (id: number) =>
    api.post(`/batch-plans/${id}/deactivate`).then((res) => res.data),
  // 从模版刷新：预览差异(只读) / 按当前模版完整替换(仅 DRAFT)
  refreshPreview: (id: number) =>
    api.get<BatchRefreshPreview>(`/batch-plans/${id}/refresh-preview`).then((res) => res.data),
  refresh: (id: number, payload?: { previewRevision?: string }) =>
    api.post<BatchRefreshResult>(`/batch-plans/${id}/refresh`, payload ?? {}).then((res) => res.data),
  // 新增：获取模版的day0偏移量
  getTemplateDay0Offset: (templateId: number) =>
    api.get<{ offset: number; min_day: number; has_pre_day0: boolean; pre_day0_count: number }>(
      `/batch-plans/templates/${templateId}/day0-offset`
    ).then((res) => res.data),
  // 新增：批量创建批次
  createBulk: (payload: {
    template_id: number;
    day0_start_date: string;
    day0_end_date: string;
    interval_days: number;
    batch_prefix: string;
    start_number: number;
    batch_number_length?: number;
    description?: string | null;
    notes?: string | null;
  }) => api.post<{ message: string; batches: any[] }>('/batch-plans/bulk', payload).then((res) => res.data),
  createBulkFromPackage: (payload: {
    mfg_package_id: number;
    base_start_date: string;
    base_end_date: string;
    interval_days: number;
    batch_prefix: string;
    start_number: number;
    batch_number_length?: number;
    project_code?: string | null;
    description?: string | null;
    notes?: string | null;
  }) => api.post<{ message: string; batches: any[] }>('/batch-plans/from-package/bulk', payload).then((res) => res.data),
};

export const shiftTypeApi = {
  getAll: () => api.get<ShiftType[]>('/shift-types').then((res) => res.data)
};

export const shiftDefinitionApi = {
  getAll: (options?: { includeInactive?: boolean }) =>
    api
      .get<ShiftDefinition[]>('/shift-definitions', {
        params: options?.includeInactive ? { includeInactive: true } : undefined,
      })
      .then((res) => res.data),
  create: (data: Omit<ShiftDefinition, 'id' | 'created_at' | 'updated_at'>) =>
    api.post<ShiftDefinition>('/shift-definitions', data).then((res) => res.data),
  update: (id: number, data: Partial<ShiftDefinition>) =>
    api.put(`/shift-definitions/${id}`, data).then((res) => res.data),
  remove: (id: number) => api.delete(`/shift-definitions/${id}`).then((res) => res.data),
};

export const specialShiftWindowApi = {
  list: (params?: { status?: string; org_unit_id?: number; start_date?: string; end_date?: string }) =>
    api.get<SpecialShiftWindow[]>('/special-shift-windows', { params }).then((res) => res.data),
  get: (id: number) =>
    api.get<SpecialShiftWindowDetail>(`/special-shift-windows/${id}`).then((res) => res.data),
  create: (payload: {
    window_name: string;
    org_unit_id: number;
    start_date: string;
    end_date: string;
    lock_after_apply?: boolean;
    notes?: string | null;
    rules: SpecialShiftWindowRule[];
  }) => api.post<SpecialShiftWindowDetail>('/special-shift-windows', payload).then((res) => res.data),
  update: (id: number, payload: {
    window_name: string;
    org_unit_id: number;
    start_date: string;
    end_date: string;
    lock_after_apply?: boolean;
    notes?: string | null;
    rules: SpecialShiftWindowRule[];
  }) => api.put<SpecialShiftWindowDetail>(`/special-shift-windows/${id}`, payload).then((res) => res.data),
  preview: (id: number) =>
    api.post<SpecialShiftWindowPreview>(`/special-shift-windows/${id}/preview`).then((res) => res.data),
  activate: (id: number) =>
    api.post<SpecialShiftWindowDetail>(`/special-shift-windows/${id}/activate`).then((res) => res.data),
  cancel: (id: number) =>
    api.post<SpecialShiftWindowDetail>(`/special-shift-windows/${id}/cancel`).then((res) => res.data),
  getOccurrences: (id: number, params?: { start_date?: string; end_date?: string; status?: string }) =>
    api.get<SpecialShiftOccurrence[]>(`/special-shift-windows/${id}/occurrences`, { params }).then((res) => res.data),
};

export const organizationApi = {
  getDepartments: () => api.get<Department[]>('/organization/departments').then((res) => res.data),
  createDepartment: (data: Partial<Department>) => api.post('/organization/departments', data).then((res) => res.data),
  updateDepartment: (id: number, data: Partial<Department>) => api.put(`/organization/departments/${id}`, data).then((res) => res.data),
  deleteDepartment: (id: number) => api.delete(`/organization/departments/${id}`).then((res) => res.data),

  getTeams: () => api.get<Team[]>('/organization/teams').then((res) => res.data),
  createTeam: (data: Partial<Team>) => api.post('/organization/teams', data).then((res) => res.data),
  updateTeam: (id: number, data: Partial<Team>) => api.put(`/organization/teams/${id}`, data).then((res) => res.data),
  deleteTeam: (id: number) => api.delete(`/organization/teams/${id}`).then((res) => res.data),

  getRoles: () => api.get<EmployeeRole[]>('/organization/roles').then((res) => res.data),
  createRole: (data: Partial<EmployeeRole>) => api.post('/organization/roles', data).then((res) => res.data),
  updateRole: (id: number, data: Partial<EmployeeRole>) => api.put(`/organization/roles/${id}`, data).then((res) => res.data),
  deleteRole: (id: number) => api.delete(`/organization/roles/${id}`).then((res) => res.data),

  getAssignments: (params?: { employeeId?: number; teamId?: number }) =>
    api.get<EmployeeTeamRole[]>('/organization/assignments', { params }).then((res) => res.data),
  createAssignment: (data: {
    employeeId: number;
    teamId: number;
    roleId: number;
    isPrimary?: boolean;
    effectiveFrom?: string;
    effectiveTo?: string | null;
  }) => api.post('/organization/assignments', data).then((res) => res.data),
  updateAssignment: (id: number, data: Partial<EmployeeTeamRole>) =>
    api.put(`/organization/assignments/${id}`, data).then((res) => res.data),
  deleteAssignment: (id: number) => api.delete(`/organization/assignments/${id}`).then((res) => res.data),

  getUnavailability: (params?: { employeeId?: number; from?: string; to?: string }) =>
    api.get<EmployeeUnavailability[]>('/organization/unavailability', { params }).then((res) => res.data),
  createUnavailability: (data: {
    employeeId: number;
    startDatetime: string;
    endDatetime: string;
    reasonCode: string;
    reasonLabel: string;
    category?: string;
    notes?: string;
  }) => api.post('/organization/unavailability', data).then((res) => res.data),
  updateUnavailability: (id: number, data: Partial<EmployeeUnavailability>) =>
    api.put(`/organization/unavailability/${id}`, data).then((res) => res.data),
  deleteUnavailability: (id: number) => api.delete(`/organization/unavailability/${id}`).then((res) => res.data)
};

export const organizationStructureApi = {
  getTree: () => api.get<OrgHierarchyResponse>('/org-structure/tree').then((res) => res.data)
};

export const organizationEmployeeApi = {
  getContext: (employeeId: number) =>
    api.get<EmployeeOrgContext>(`/employees/${employeeId}/organization-context`).then((res) => res.data)
};

export const systemMonitorApi = {
  getHolidayStatus: () => api.get<HolidayServiceStatus>('/system/holiday/status').then((res) => res.data),
  updateHolidayKey: (payload: { apiKey: string }) =>
    api.patch<{ keyConfigured: boolean; maskedKey: string | null }>('/system/holiday/key', payload).then((res) => res.data),
  importHolidayYear: (payload: { year: number }) =>
    api.post('/system/holiday/import', payload).then((res) => res.data),
};

export const systemSettingsApi = {
  getHolidayStatus: () => api.get<HolidayServiceStatus>('/system/holiday/status').then((res) => res.data),
  updateHolidayKey: (payload: { apiKey: string }) =>
    api.patch<{ keyConfigured: boolean; maskedKey: string | null }>('/system/holiday/key', payload).then((res) => res.data),
  importHolidayYear: (payload: { year: number }) =>
    api.post('/system/holiday/import', payload).then((res) => res.data),
  getSchedulingSettings: () => api.get<SchedulingSettings>('/system/scheduling/settings').then((res) => res.data),
  updateSchedulingSettings: (data: SchedulingSettings) =>
    api.put<SchedulingSettings>('/system/scheduling/settings', data).then((res) => res.data),

  // DB Config
};

export const calendarApi = {
  getActiveOperations: () => api.get('/calendar/operations/active').then((res) => res.data),
  getOperationDetail: (operationPlanId: number) =>
    api.get(`/calendar/operations/${operationPlanId}`).then((res) => res.data),
  getWorkdayRange: (startDate: string, endDate: string) =>
    api
      .get('/calendar/workdays', {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      })
      .then((res) => res.data),
};

/**
 * 共享的 axios 实例（已装配 Bearer 注入 + 401/403 拦截）。
 * authApi / governanceApi 等新服务模块复用此实例，避免重复创建/重复装拦截器。
 */
export default api;
